/**
 * Is the page in a state where the browser itself kills in-flight requests?
 *
 * Safari reports an XHR it tore down because the tab was hidden, suspended,
 * put into the back/forward cache or unloaded as a plain `Network Error` with
 * no status — indistinguishable, at the axios level, from our API being down.
 * Monitoring group #1177 was exactly that: a Kanban card opens the request in
 * a NEW TAB (window.open, KanbanRequests.jsx), the Kanban tab goes to the
 * background, and the burst of refreshes that tab-lifecycle events trigger
 * (Onboarding on visibilitychange/focus, react-query on reconnect) died with
 * it — three unrelated endpoints, "UNREACHABLE", in the same instant.
 *
 * The classifier asks this module before calling a no-status failure an
 * outage. It only answers "yes" while the page is unloading, while it is
 * hidden, or for a few seconds either side of a lifecycle transition; on a
 * visible page that has been live for longer than that, a no-status failure
 * is still reported as UNREACHABLE.
 */

// How long after a transition (hidden ↔ visible, bfcache restore, network
// back online) a failure is still attributed to it. Long enough for Safari to
// re-establish connections after resuming a suspended tab; short enough that
// a real outage is still caught by the next request or poll.
export const TRANSITION_GRACE_MS = 5000;

let unloading = false;
let lastTransitionAt = -Infinity;
let installed = false;

const markTransition = () => {
  lastTransitionAt = Date.now();
};

function install() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const opts = { capture: true, passive: true };

  // pagehide is the reliable "this document is going away" signal (Safari
  // does not always fire unload, and pagehide also covers bfcache entry).
  window.addEventListener(
    "pagehide",
    () => {
      unloading = true;
      markTransition();
    },
    opts
  );
  // Restored from the back/forward cache, or the unload never happened.
  window.addEventListener(
    "pageshow",
    () => {
      unloading = false;
      markTransition();
    },
    opts
  );
  // beforeunload can be cancelled ("unsaved changes"), so it only opens the
  // grace window rather than marking the page as unloading for good.
  window.addEventListener("beforeunload", markTransition, opts);
  // react-query refetches every stale query on `online`; on wake-from-sleep
  // that burst often goes out before the network is actually usable.
  window.addEventListener("online", markTransition, opts);

  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", markTransition, opts);
    // Chromium's page-lifecycle freeze/resume; harmless elsewhere.
    document.addEventListener("freeze", markTransition, opts);
    document.addEventListener("resume", markTransition, opts);
  }
}

install();

/** The document is being unloaded or stashed in the back/forward cache. */
export function isPageUnloading() {
  return unloading;
}

/**
 * The page is unloading, hidden, or within TRANSITION_GRACE_MS of a lifecycle
 * transition — so a request that failed with no response was most likely
 * killed by the browser, not by the network or our API.
 */
export function isPageInterrupted(now = Date.now()) {
  if (unloading) return true;
  if (typeof document !== "undefined" && document.visibilityState === "hidden") {
    return true;
  }
  return now - lastTransitionAt < TRANSITION_GRACE_MS;
}
